import { Core } from "@faire/design-tokens";
import { Opacity } from "@faire/web--source/slate/Color";
import React, { useEffect } from "react";
import ReactModal from "react-modal";
import { setAppElement } from "react-modal";
import styled, { css } from "styled-components";

let reactModalInitialized = false;

/**
 * Calls ReactModal.setAppElement which is required before showing any ReactModal dialog.
 * This function can be safely called many times.
 * It should not be called until we actually try to show a modal, in order to defer loading
 * all the react-modal JS.
 */
const initializeReactModal = () => {
  // eslint-disable-next-line no-process-env
  if (process.env.NODE_ENV !== "test" && !reactModalInitialized) {
    setAppElement("#root");
    reactModalInitialized = true;
  }
};

const Container = styled.div`
  display: flex;
  align-items: center;
  background-color: ${Core.surface.inverse};
  box-shadow: 4px 0 10px ${Core.surface.default + Opacity.O_20};
  width: 100%;
  height: fit-content;
`;

interface IProps extends ReactModal.Props {
  className?: string;
}

const ReactModalAdapter: React.FC<IProps> = (props) => {
  const { className, children, ...modalProps } = props;

  useEffect(() => {
    initializeReactModal();
  }, []);

  const contentClassName = `${className}__content`;
  const overlayClassName = `${className}__overlay`;
  return (
    <ReactModal
      portalClassName={className}
      className={contentClassName}
      overlayClassName={overlayClassName}
      closeTimeoutMS={500}
      {...modalProps}
    >
      <Container>{children}</Container>
    </ReactModal>
  );
};

export const WrappedModal = styled(ReactModalAdapter)<{
  bottom: number;
  showOverlay: boolean;
}>`
  ${(props) =>
    props.showOverlay
      ? css`
          &__overlay {
            position: fixed;
            top: 0;
            width: 100vw;
            height: 100vh;
            z-index: 11;
            background-color: rgba(0, 0, 0, 0.5);
          }
        `
      : ""}

  &__content {
    position: fixed;
    bottom: ${(props) => (props.bottom ? props.bottom : 0)}px;
    width: 100%;
    z-index: 11;
    outline: none;

    transition-duration: 0.3s;
    transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    transition-property: transform;
    transform: translateY(100%);

    &.ReactModal__Content--after-open {
      transform: translateY(0);
    }

    &.ReactModal__Content--before-close {
      transform: translateY(100%);
    }
  }
`;
